import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import { toast } from "react-toastify";
import { io, Socket } from "socket.io-client";
import { getLoginData } from "../utility/AppStoreData";

interface WsConfig {
  ip: string;
  port: number;
}

interface StreamConfig {
  twitchUrl?: string;
  twitchStreamKey?: string;
  youtubeUrl?: string;
  youtubeStreamKey?: string;
}

type NativeAppConnectionState =
  | "native-app-not-connected"
  | "native-app-connecting"
  | "native-app-connected"
  | "native-app-disconnected";

type NativeAppState =
  | "service-starting"
  | "idle"
  | "streaming-starting"
  | "streaming"
  | "network-disconnected"
  | "network-reconnecting"
  | "restarting-streaming"
  | "service-error"
  | "service-restarting"
  | "streaming-error";

interface NativeAppStatus {
  state: NativeAppState;
  isStreaming: boolean;
  isRecording: boolean;
  isConnected: boolean;
  recordingFile?: string;
  error?: string;
}

interface WsContextType {
  connectWs: (config?: WsConfig, silent?: boolean) => Promise<void>;
  connectWithDeeplink: () => Promise<void>;
  disconnectWs: () => void;
  nativeAppConnectionState: NativeAppConnectionState;
  nativeAppStatus: NativeAppStatus | null;
  wsConfig: WsConfig;
  ws: Socket | null;
  loginData: any;
  sendMessage: (type: string, data: any) => void;
  startStreaming: (youtubeKey?: string, twitchKey?: string) => Promise<void>;
  stopStreaming: () => void;
  streamStarted: boolean;
  streamConfig: StreamConfig;
  setStreamConfig: (config: Partial<StreamConfig>) => void;
  startRecording: boolean;
  recordingFile?: string;
  toggleRecording: () => Promise<void>;
  smartReconnect: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  retryConnection: () => Promise<void>;
  // Helper functions for UI
  isNativeAppConnected: boolean;
  canStartStreaming: boolean;
  canStopStreaming: boolean;
  canStartRecording: boolean;
  canStopRecording: boolean;
  shouldShowReconnect: boolean;
  // Broadcaster ID for relay connection
  broadcasterId: string | null;
  // Callback for streaming status changes
  setOnStreamingStatusChange: (
    callback: ((isStreaming: boolean) => void) | null
  ) => void;
}

const WsContext = createContext<WsContextType | undefined>(undefined);

interface WsProviderProps {
  children: ReactNode;
}

export const WsProvider: React.FC<WsProviderProps> = ({ children }) => {
  const [wsConfig, setWsConfig] = useState<WsConfig>({
    ip: "localhost",
    port: 5100,
  });
  const [ws, setWs] = useState<Socket | null>(null);
  const [loginData, setLoginData] = useState<any>(null);
  const [nativeAppConnectionState, setNativeAppConnectionState] =
    useState<NativeAppConnectionState>("native-app-not-connected");
  const [nativeAppStatus, setNativeAppStatus] =
    useState<NativeAppStatus | null>(null);
  const [streamStarted, setStreamStarted] = useState<boolean>(false);
  const [startRecording, setStartRecording] = useState<boolean>(false);
  const [recordingFile, setRecordingFile] = useState<string | undefined>(
    undefined
  );
  const [streamConfig, setStreamConfig] = useState<StreamConfig>({});
  const [error, setError] = useState<string | null>(null);
  const [broadcasterId, setBroadcasterId] = useState<string | null>(null);
  const isManuallyDisconnectedRef = useRef<boolean>(false);
  const socketRef = useRef<Socket | null>(null);
  const connectionRetryCount = useRef<number>(0);
  const maxRetryAttempts = 3;
  const retryDelay = 2000;
  // Callback ref for streaming status changes
  const onStreamingStatusChangeRef = useRef<
    ((isStreaming: boolean) => void) | null
  >(null);
  const prevStreamingStateRef = useRef<boolean | null>(null);

  const openDeeplink = useCallback(() => {
    return new Promise<void>((resolve) => {
      // Create hidden iframe to trigger deeplink
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = "ko-broadcaster://start";
      document.body.appendChild(iframe);

      // Remove iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
        resolve();
      }, 1000);
    });
  }, []);

  const connectToSocket = useCallback(
    async (silent: boolean = false): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        try {
          const wsUrl = `http://${wsConfig.ip}:${wsConfig.port}`;

          const socket = io(wsUrl, {
            reconnection: false,
            transports: ["websocket"],
            timeout: 5000,
            forceNew: true,
          });

          socketRef.current = socket;
          setWs(socket);

          socket.once("connect", async () => {
            console.log("✅ Connected to KO Broadcaster services");
            setNativeAppConnectionState("native-app-connected");
            setError(null);
            isManuallyDisconnectedRef.current = false;
            connectionRetryCount.current = 0;

            try {
              const data = await getLoginData();
              setLoginData(data);
              socket.emit("get-status");

              // Fetch broadcaster ID for relay connection
              socket.emit(
                "get-broadcaster-id",
                (response: {
                  success: boolean;
                  broadcasterId?: string;
                  error?: string;
                }) => {
                  if (response.success && response.broadcasterId) {
                    console.log(
                      "✅ Got broadcaster ID:",
                      response.broadcasterId
                    );
                    setBroadcasterId(response.broadcasterId);
                  } else {
                    console.error(
                      "Failed to get broadcaster ID:",
                      response.error
                    );
                  }
                }
              );

              resolve();
            } catch (err) {
              console.error("Error fetching login data:", err);
              setError("Failed to authenticate connection");
              reject(err);
            }
          });

          socket.once("connect_error", (err) => {
            console.error("❌ WebSocket connection error:", err);
            if (!silent) {
              setNativeAppConnectionState("native-app-not-connected");
            }
            reject(err);
          });

          socket.on("disconnect", (reason) => {
            console.log("WebSocket disconnected:", reason);
            setNativeAppConnectionState("native-app-disconnected");
            setNativeAppStatus(null);
            setStreamStarted(false);
            setStartRecording(false);
            setRecordingFile(undefined);
            if (!isManuallyDisconnectedRef.current) {
              setError("WebSocket disconnected unexpectedly");
            }
          });

          socket.on("status-update", (status: NativeAppStatus) => {
            console.log("Status update:", status);
            setNativeAppStatus(status);
            setStreamStarted(status.isStreaming);
            setStartRecording(status.isRecording);
            setRecordingFile(status.recordingFile);
            if (status.error) {
              setError(status.error);
            } else {
              setError(null);
            }

            // Notify external listeners about streaming status changes
            if (prevStreamingStateRef.current !== status.isStreaming) {
              prevStreamingStateRef.current = status.isStreaming;
              if (onStreamingStatusChangeRef.current) {
                onStreamingStatusChangeRef.current(status.isStreaming);
              }
            }
          });

          socket.on(
            "obs-websocket-password",
            (response: { password?: string; error?: string }) => {
              if (response.error) {
                setError(response.error);
              } else {
                setError(null);
              }
            }
          );
        } catch (err) {
          console.error("WebSocket setup error:", err);
          reject(err);
        }
      });
    },
    [wsConfig]
  );

  const connectWs = useCallback(
    async (config?: WsConfig, silent: boolean = false): Promise<void> => {
      if (nativeAppConnectionState === "native-app-connecting") {
        console.warn("Connection attempt already in progress");
        return;
      }

      if (config) {
        setWsConfig(config);
      }

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      if (!silent) {
        setNativeAppConnectionState("native-app-connecting");
      }
      setError(null);

      try {
        // Only attempt direct socket connection
        await connectToSocket(silent);
      } catch (err) {
        console.log("Direct connection failed");
        if (!silent) {
          setNativeAppConnectionState("native-app-not-connected");
          setError("Failed to connect to KO Broadcaster services");
        }
        throw err;
      }
    },
    [nativeAppConnectionState, connectToSocket, wsConfig]
  );

  const disconnectWs = useCallback(() => {
    if (ws) {
      isManuallyDisconnectedRef.current = true;
      ws.disconnect();
      setWs(null);
    }
    setNativeAppConnectionState("native-app-not-connected");
    setNativeAppStatus(null);
    setLoginData(null);
    setStreamStarted(false);
    setStartRecording(false);
    setRecordingFile(undefined);
    setStreamConfig({});
    setError(null);
    setBroadcasterId(null);
  }, [ws]);

  const sendMessage = useCallback(
    (type: string, data: any) => {
      if (
        ws &&
        ws.connected &&
        nativeAppConnectionState === "native-app-connected"
      ) {
        ws.emit(type, { type, data });
      } else {
        setError("Not connected to KO Broadcaster. Please connect first.");
      }
    },
    [ws, nativeAppConnectionState]
  );

  const startStreaming = useCallback(
    async (youtubeKey?: string, twitchKey?: string): Promise<void> => {
      if (
        !ws ||
        !ws.connected ||
        nativeAppConnectionState !== "native-app-connected"
      ) {
        setError("Not connected to KO Broadcaster. Please connect first.");
        return;
      }
      if (!youtubeKey && !twitchKey) {
        setError("Please provide at least one stream key");
        return;
      }
      return new Promise((resolve) => {
        ws.emit(
          "start-streaming",
          { youtubeKey, twitchKey },
          (response: { success: boolean; error?: string }) => {
            if (response.success) {
              setStreamStarted(true);
              setError(null);
            } else {
              setError(response.error || "Failed to start streaming");
            }
            resolve();
          }
        );
      });
    },
    [ws, nativeAppConnectionState]
  );

  const stopStreaming = useCallback(() => {
    if (
      !ws ||
      !ws.connected ||
      nativeAppConnectionState !== "native-app-connected"
    ) {
      setError("Not connected to KO Broadcaster. Please connect first.");
      return;
    }
    ws.emit(
      "stop-streaming",
      (response: { success: boolean; error?: string }) => {
        if (response.success) {
          setStreamStarted(false);
          setError(null);
        } else {
          setError(response.error || "Failed to stop streaming");
        }
      }
    );
  }, [ws, nativeAppConnectionState]);

  const toggleRecording = useCallback(async (): Promise<void> => {
    if (
      !ws ||
      !ws.connected ||
      nativeAppConnectionState !== "native-app-connected"
    ) {
      setError("Not connected to KO Broadcaster. Please connect first.");
      return;
    }
    const event = startRecording ? "stop-recording" : "start-recording";
    return new Promise((resolve) => {
      ws.emit(event, {}, (response: { success: boolean; error?: string }) => {
        if (response.success) {
          setStartRecording(!startRecording);
          setError(null);
        } else {
          setError(
            response.error || `Failed to ${event.split("-")[0]} recording`
          );
        }
        resolve();
      });
    });
  }, [ws, startRecording, nativeAppConnectionState]);

  const smartReconnect = useCallback(async (): Promise<void> => {
    if (
      !ws ||
      !ws.connected ||
      nativeAppConnectionState !== "native-app-connected"
    ) {
      setError("Not connected to KO Broadcaster. Please connect first.");
      return;
    }
    return new Promise((resolve) => {
      ws.emit(
        "smart-reconnect",
        (response: { success: boolean; error?: string }) => {
          if (!response.success) {
            setError(response.error || "Failed to reconnect");
          } else {
            setError(null);
          }
          resolve();
        }
      );
    });
  }, [ws, nativeAppConnectionState]);

  const connectWithDeeplink = useCallback(async (): Promise<void> => {
    if (nativeAppConnectionState === "native-app-connecting") {
      console.warn("Connection attempt already in progress");
      return;
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setNativeAppConnectionState("native-app-connecting");
    setError(null);

    try {
      // First attempt: direct socket connection
      await connectToSocket();
    } catch (err) {
      console.log("Direct connection failed, trying deeplink + retry strategy");

      // If direct connection fails, try deeplink + retry
      try {
        await openDeeplink();

        // Retry connection up to maxRetryAttempts times with delay
        for (let i = 0; i < maxRetryAttempts; i++) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          try {
            await connectToSocket();
            return; // Success!
          } catch (retryErr) {
            console.log(`Retry attempt ${i + 1} failed:`, retryErr);
            if (i === maxRetryAttempts - 1) {
              throw retryErr; // Last attempt failed
            }
          }
        }
      } catch (finalErr) {
        console.error("All connection attempts failed:", finalErr);
        setNativeAppConnectionState("native-app-not-connected");
        setError(
          "Failed to connect to KO Broadcaster services. Please ensure KO Broadcaster is installed and running."
        );
        throw finalErr;
      }
    }
  }, [nativeAppConnectionState, connectToSocket, openDeeplink]);

  const retryConnection = useCallback(async (): Promise<void> => {
    disconnectWs();
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return connectWithDeeplink();
  }, [connectWithDeeplink, disconnectWs]);

  // Auto-connection on mount only (silent)
  useEffect(() => {
    // Initial connection attempt (silent - won't show connecting state)
    connectWs(undefined, true).catch((err) => {
      console.log("Initial auto-connection failed:", err);
    });
  }, [connectWs]);

  // Optional: Less frequent background check (every 30 seconds)
  useEffect(() => {
    if (nativeAppConnectionState !== "native-app-not-connected") {
      return;
    }

    const retryInterval = setInterval(() => {
      if (nativeAppConnectionState === "native-app-not-connected") {
        console.log("Background connection check...");
        // Use silent connection attempt
        connectWs(undefined, true).catch(() => {});
      }
    }, 30000); // 30 seconds instead of 10

    return () => clearInterval(retryInterval);
  }, [nativeAppConnectionState, connectWs]);

  // Helper computed values for UI
  const isNativeAppConnected =
    nativeAppConnectionState === "native-app-connected";
  const canStartStreaming =
    isNativeAppConnected &&
    nativeAppStatus?.state === "idle" &&
    !nativeAppStatus?.isStreaming;
  const canStopStreaming = isNativeAppConnected && nativeAppStatus?.isStreaming;
  const canStartRecording =
    isNativeAppConnected &&
    nativeAppStatus &&
    ["idle", "streaming"].includes(nativeAppStatus.state) &&
    !nativeAppStatus.isRecording;
  const canStopRecording = isNativeAppConnected && nativeAppStatus?.isRecording;
  const shouldShowReconnect =
    isNativeAppConnected &&
    nativeAppStatus &&
    ["service-error", "streaming-error", "network-disconnected"].includes(
      nativeAppStatus.state
    );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateStreamConfig = useCallback((config: Partial<StreamConfig>) => {
    setStreamConfig((prev) => ({ ...prev, ...config }));
  }, []);

  const setOnStreamingStatusChange = useCallback(
    (callback: ((isStreaming: boolean) => void) | null) => {
      onStreamingStatusChangeRef.current = callback;
    },
    []
  );

  return (
    <WsContext.Provider
      value={{
        connectWs,
        connectWithDeeplink,
        disconnectWs,
        nativeAppConnectionState,
        nativeAppStatus,
        wsConfig,
        ws,
        loginData,
        sendMessage,
        startStreaming,
        stopStreaming,
        streamStarted,
        streamConfig,
        setStreamConfig: updateStreamConfig,
        startRecording,
        recordingFile,
        toggleRecording,
        smartReconnect,
        error,
        clearError,
        retryConnection,
        // Helper properties for UI
        isNativeAppConnected,
        canStartStreaming,
        canStopStreaming,
        canStartRecording,
        canStopRecording,
        shouldShowReconnect,
        broadcasterId,
        setOnStreamingStatusChange,
      }}
    >
      {children}
    </WsContext.Provider>
  );
};

export const useWsProvider = (): WsContextType => {
  const context = useContext(WsContext);
  if (!context)
    throw new Error("useWsProvider must be used within a WsProvider");
  return context;
};
